import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Phone, WhatsApp, Email, LocationOn, Schedule, Message } from '@mui/icons-material';

const ContactWrapper = styled.div`
  max-width: 1200px;
  margin: 4rem auto;
  padding: 0 2rem;
`;

const ContactTitle = styled.h1`
  color: ${props => props.theme.colors.darkGreen};
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1rem;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 50%;
    transform: translateX(50%);
    width: 100px;
    height: 3px;
    background: ${props => props.theme.colors.darkGreen};
  }
`;

const ContactSubtitle = styled.p`
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-bottom: 4rem;
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
`;

const ContactCard = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 15px;
  text-decoration: none;
  color: ${props => props.theme.colors.text};
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.small};

  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.color || props.theme.colors.darkGreen};
    box-shadow: ${props => props.theme.shadows.medium};
  }

  .icon-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    background: ${props => props.lightColor || props.theme.colors.lightGray};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.color || props.theme.colors.darkGreen};

    svg {
      font-size: 2rem;
    }
  }

  .text-content {
    h3 {
      margin: 0 0 0.5rem 0;
      font-size: 1.3rem;
    }
    p {
      margin: 0;
      color: #666;
      font-size: 1rem;
    }
  }
`;

const ContactInfo = styled.div`
  background: ${props => props.theme.colors.darkGreen};
  color: white;
  padding: 3rem;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  height: fit-content;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 1.1rem;

  svg {
    font-size: 1.8rem;
    opacity: 0.8;
  }

  .info-text {
    h4 {
      margin: 0 0 0.2rem 0;
      font-size: 0.9rem;
      opacity: 0.7;
      text-transform: uppercase;
    }
    p {
      margin: 0;
      font-weight: 500;
    }
  }
`;

const Contact = () => {
  return (
    <ContactWrapper>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <ContactTitle>נעים להכיר, אני אפיק</ContactTitle>
        <ContactSubtitle>זמינה עבורכם לכל שאלה בדרך למשכנתא האופטימלית</ContactSubtitle>
      </motion.div>

      <ContactContent>
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <ActionGrid>
            <ContactCard 
              href="https://wa.me/972548827511?text=שלום אפיק, הגעתי מהאתר ואשמח להתייעץ איתך"
              target="_blank"
              color="#25D366"
              lightColor="rgba(37, 211, 102, 0.1)"
              whileHover={{ scale: 1.02 }}
            >
              <div className="icon-wrapper">
                <WhatsApp />
              </div>
              <div className="text-content">
                <h3>שלחו לי הודעה</h3>
                <p>הדרך המהירה ביותר לקבל מענה</p>
              </div>
            </ContactCard>

            <ContactCard 
              href="tel:0548827511"
              color="#2F4F4F"
              lightColor="rgba(47, 79, 79, 0.1)"
              whileHover={{ scale: 1.02 }}
            >
              <div className="icon-wrapper">
                <Phone />
              </div>
              <div className="text-content">
                <h3>התקשרו אלי</h3>
                <p>זמינה לשיחה אישית בטלפון</p>
              </div>
            </ContactCard>

            <ContactCard 
              href="mailto:afiklev@gmail.com"
              color="#D2A46E"
              lightColor="rgba(210, 164, 110, 0.1)"
              whileHover={{ scale: 1.02 }}
            >
              <div className="icon-wrapper">
                <Email />
              </div>
              <div className="text-content">
                <h3>שלחו מייל</h3>
                <p>לשאלות מורכבות או משלוח מסמכים</p>
              </div>
            </ContactCard>
          </ActionGrid>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <ContactInfo>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.8rem' }}>פרטי התקשרות</h3>
            
            <InfoItem>
              <LocationOn />
              <div className="info-text">
                <h4>מיקום</h4>
                <p>שירות בפריסה ארצית</p>
              </div>
            </InfoItem>

            <InfoItem>
              <Schedule />
              <div className="info-text">
                <h4>שעות פעילות</h4>
                <p>א' - ה': 09:00 - 20:00</p>
              </div>
            </InfoItem>

            <InfoItem>
              <Message />
              <div className="info-text">
                <h4>רשתות חברתיות</h4>
                <p>חפשו "אפיק לב - ייעוץ משכנתאות"</p>
              </div>
            </InfoItem>

            <div style={{ marginTop: 'auto', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <p style={{ fontSize: '0.9rem', opacity: 0.8, lineHeight: '1.6' }}>
                * ניתן לתאם פגישות פרונטליות או שיחות זום בתיאום מראש.
              </p>
            </div>
          </ContactInfo>
        </motion.div>
      </ContactContent>
    </ContactWrapper>
  );
};

export default Contact;
